import { BODY_TYPE, MINIMAP_BIG_SCALES, type Size } from "odyssey-shared";
import { maxNearbyField } from "./levels.ts";

/**
 * The Nearby level's pure parts (ADR-100's amendment of 2026-10-14, decisions 41, 44 and 45): the strip the legend takes
 * below the field, the field the window's body leaves for it, the largest window the `nearby` crop can cover, and the
 * wheel's and the pinch's steps of the ladder. No React, no DOM.
 */

/**
 * The gap between the field and the legend, in logical pixels.
 */
export const LEGEND_GAP = 3;

/**
 * A legend line's height: the caption type's line.
 */
export const LEGEND_LINE = BODY_TYPE.caption.lineHeight;

/**
 * The room a legend entry's swatch and the gap after it take, and an estimate of a character of the caption type, in
 * logical pixels. The legend wraps (P1.9-T180), so its height is estimated from these, and the estimate runs wide so a
 * line is never short. The names on the map are not estimated: their boxes are the small pixel face's real widths
 * (`nameBox`).
 */
const ENTRY_SWATCH = 5 + 2;
const ENTRY_GAP = 6;
const ENTRY_CHAR = 5;

/**
 * How many lines a legend of these words takes in a width, packing entries greedily as the legend's flex row wraps them.
 *
 * @param width The room in logical pixels.
 * @param words The words of every entry.
 * @returns The lines, at least one for each row that fits an entry, 0 for no words.
 */
function legendLines(width: number, words: readonly string[]): number {
  if (words.length === 0) {
    return 0;
  }
  let lines = 1;
  let used = 0;
  for (const word of words) {
    const entry = ENTRY_SWATCH + word.length * ENTRY_CHAR;
    const next = used === 0 ? entry : used + ENTRY_GAP + entry;
    if (next > width && used > 0) {
      lines += 1;
      used = entry;
    } else {
      used = next;
    }
  }
  return lines;
}

/**
 * The strip a legend takes under the field: its gap and its lines. The window gives it the words of every entry that can
 * ever be listed, so the strip does not move as kinds come into view and the field is not remade for it.
 *
 * @param width The body's width in logical pixels.
 * @param words The words of every entry the legend can list.
 * @returns Logical pixels, 0 for no words.
 */
export function legendStrip(width: number, words: readonly string[]): number {
  const lines = legendLines(width, words);
  return lines === 0 ? 0 : LEGEND_GAP + lines * LEGEND_LINE;
}

/**
 * The Nearby field a body leaves: the body less the legend's strip, held to what the `nearby` crop covers at the scale.
 *
 * @param body The body's size in logical pixels.
 * @param strip The legend's strip.
 * @param scale Logical pixels a tile.
 * @returns The field, whole pixels; a side of 0 where the body leaves none.
 */
export function nearbyField(body: Size, strip: number, scale: number): Size {
  const most = maxNearbyField(scale);
  return { width: Math.max(0, Math.min(Math.floor(body.width), most.width)), height: Math.max(0, Math.min(Math.floor(body.height - strip), most.height)) };
}

/**
 * The tallest legend the window plans for, in lines, which bounds the largest window.
 */
export const LEGEND_LINES_MOST = 3;

/**
 * The Map window's largest size: the field the crop covers at the finest Nearby scale (the smallest any scale's crop
 * covers), the tallest legend and the window's chrome, so no scale is ever asked for a field its crop cannot cover.
 * This is below the kit's table maximum, which the frame takes the smaller of.
 *
 * @param chrome What the window takes beyond its body: its width and height less the view's at the default size.
 * @returns The size in logical pixels.
 */
export function mapWindowMost(chrome: Size): Size {
  const finest = MINIMAP_BIG_SCALES.reduce((least, scale) => Math.min(least, maxNearbyField(scale).width), Number.POSITIVE_INFINITY);
  return { width: finest + chrome.width, height: finest + LEGEND_GAP + LEGEND_LINES_MOST * LEGEND_LINE + chrome.height };
}

/**
 * The scrolled distance, in pixels, that is one step of the ladder.
 */
export const WHEEL_STEP = 50;

const WHEEL_LINE = 17;
const WHEEL_PAGE = 100;

/**
 * Turns a wheel's travel into a step of the ladder: scrolled up zooms in, down zooms out, and a trackpad's small deltas
 * add up to a step, the part of one left over carried to the next event. One event is at most one step, so a notch of a
 * mouse wheel, whatever its size in the browser, is one level, and a fling is as many as it has events.
 *
 * @param held The travel carried from the last event, in pixels, positive down.
 * @param delta The event's `deltaY`.
 * @param mode The event's `deltaMode`: 0 pixels, 1 lines, 2 pages.
 * @returns The `steps` to take (1 in, -1 out or 0) and the `rest` to carry, which a step clears.
 */
export function wheelSteps(held: number, delta: number, mode: number): { readonly steps: 1 | 0 | -1; readonly rest: number } {
  if (!Number.isFinite(delta)) {
    return { steps: 0, rest: 0 };
  }
  const travel = held + delta * (mode === 1 ? WHEEL_LINE : mode === 2 ? WHEEL_PAGE : 1);
  if (Math.abs(travel) < WHEEL_STEP) {
    return { steps: 0, rest: travel };
  }
  return { steps: travel > 0 ? -1 : 1, rest: 0 };
}

/**
 * What a two finger pinch asks: fingers a third further apart than they started are a step in, a quarter closer a step
 * out, and anything between nothing.
 *
 * @param start The distance between the fingers when the pinch began, or was last taken as a step.
 * @param distance The distance now.
 * @returns 1 to zoom in, -1 to zoom out, 0 for neither.
 */
export function pinchStep(start: number, distance: number): 1 | 0 | -1 {
  if (!(start > 0) || !Number.isFinite(distance)) {
    return 0;
  }
  const ratio = distance / start;
  return ratio >= 4 / 3 ? 1 : ratio <= 3 / 4 ? -1 : 0;
}
